import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Btn, Chip, Eyebrow, Ornament, Tagline, Seal, LiveDot, Diamond } from "@/components/brand";
import { useSite, useSeo, Ticker, DEFAULT_SQUARE } from "@/components/site";
import { track, money } from "@/lib/api";

export const FAQ = [
  ["What happens on my free consultation?", "You talk with me, Seven, directly. I look at your goals and where you are today, explain exactly what I would go after first, and tell you which option fits. No pressure, no cost."],
  ["I've been burned by another credit company. Why is Se7en different?", "Two things. First, you have an open line to me the whole time: message me or request a call anytime. Most companies go quiet once you pay; I don't. Second, you see everything: your scores, every account and every verified result, live in your portal."],
  ["How fast can I get started?", "Today. Book your consultation, enroll through our MyFreeScoreNow link, and I start on your report right away. We go after everything in one complete sweep, as fast as 4–7 days."],
  ["Can I really afford this?", "Yes. We are the fastest and most affordable in the industry. Pay through Square with any card, Cash App Pay or Apple Pay, or split it into interest-free installments with Afterpay. If you need a different plan, I'll work with you."],
  ["I'm starting from scratch. Can you still help?", "That's who I love working with. Low scores, collections, lots of inquiries: I build the plan from wherever you are."],
  ["What comes after my credit is fixed?", "Funding. Once your profile is clean, we help you go after $20k–$100k+ in personal and business funding. If you need an LLC for it, I can set that up too."],
];

function Hero() {
  return (
    <section className="bg-tan" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 lg:py-24 grid lg:grid-cols-[1.15fr_.85fr] gap-14 items-center">
        <div className="fade-up">
          <p className="eyebrow text-red7">— Credit Repair · Inquiry Sweeps · Funding</p>
          <h1 className="f-display text-[44px] leading-[1.02] sm:text-6xl lg:text-[64px] mt-6 font-medium">
            Strategic leverage for your <em className="text-red7 font-medium">financial</em> future.
          </h1>
          <p className="text-base sm:text-lg text-muted7 mt-6 max-w-xl leading-relaxed">We go after everything in one complete sweep — then help you turn your new credit into $20k–$100k+ in funding. Work directly with me, Seven. No call centers. No runaround.</p>
          <div className="flex flex-wrap gap-2 mt-7">
            <Chip tone="dark" className="!py-2">Fastest & most affordable in the industry</Chip>
            <Chip className="!py-2">Results in as little as 4–7 days</Chip>
            <Chip className="!py-2">Interest-free installments</Chip>
          </div>
          <div className="flex flex-wrap gap-3 mt-9">
            <Btn as={Link} to="/consultation" arrow data-testid="hero-consult-btn" onClick={() => track("cta_click", { label: "hero_consult" })}>Book Free Consultation</Btn>
            <Btn as="a" href="#pricing" variant="outline" data-testid="hero-pricing-btn">See Pricing</Btn>
          </div>
        </div>
        <div className="relative mx-auto w-full max-w-[380px] lg:max-w-[420px] fade-up" style={{ animationDelay: ".12s" }}>
          <div className="bg-ink p-4 sm:p-5 shadow-[0_30px_60px_-30px_rgba(17,17,17,.6)]">
            <div className="border border-gold/50 p-3 sm:p-4">
              <img src="/brand/logo.jpg" alt="Se7en Capital Group logo — credit repair, funding, business opening and LLC creation, financial consulting, real estate strategies" width="518" height="1026"
                className="w-full h-auto object-contain drop-shadow-[0_12px_24px_rgba(0,0,0,.55)]" fetchpriority="high" data-testid="hero-logo" />
            </div>
          </div>
          <div className="absolute -top-8 right-0 sm:-right-8"><Seal size={96} /></div>
          <div className="absolute -bottom-8 left-0 sm:-left-12 bg-white border-l-4 border-red7 px-5 py-4 shadow-xl max-w-[260px]" data-testid="hero-talk-card">
            <p className="eyebrow text-[9px] text-muted7">Open line of communication</p>
            <p className="f-display text-2xl mt-1.5">Talk to Seven anytime.</p>
            <LiveDot className="mt-2" />
          </div>
        </div>
      </div>
    </section>
  );
}

const SERVICES = [
  ["bg-ink", "Credit Repair", "We challenge inaccurate, obsolete and unverifiable items at all three bureaus — everything at once, in one complete sweep.", "4–7 days · 14 days · 21–30 days"],
  ["bg-red7", "Inquiry Sweep", "Unverified hard inquiries removed across Equifax, TransUnion and Experian.", "$97 — for everyone"],
  ["bg-gold", "Funding", "Once your credit is fixed, we help you go after $20k–$100k+ in personal and business funding.", "$20k – $100k+"],
  ["bg-tanline", "LLC Setup", "Already have an LLC? Great. If you need one for business funding, I can set it up as an extra.", "Only if needed"],
];

function Services() {
  return (
    <section id="services" className="bg-white py-20 lg:py-28 scroll-mt-20" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Eyebrow n="01">What we do</Eyebrow>
        <h2 className="f-display text-4xl sm:text-5xl mt-4 max-w-2xl">Fix it. Keep it clean. Get funded.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {SERVICES.map(([bar, t, d, f]) => (
            <article key={t} className="bg-tancard border border-tanline lift flex flex-col" data-testid={`service-card-${t.toLowerCase().replace(/\s/g, "-")}`}>
              <div className={`h-[3px] ${bar}`} />
              <div className="p-7 flex-1 flex flex-col">
                <h3 className="f-display text-2xl">{t}</h3>
                <p className="text-[15px] text-muted7 mt-3 leading-relaxed flex-1">{d}</p>
                <p className="f-mono text-[11px] tracking-[0.14em] text-goldtext mt-6 pt-4 border-t border-tanline uppercase">{f}</p>
              </div>
            </article>
          ))}
        </div>
        <p className="f-mono text-[11px] tracking-[0.16em] uppercase mt-8 text-muted7 flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="text-ink font-semibold">Also offering:</span> Financial consulting <Diamond /> Real estate strategies <Diamond /> Business opening
        </p>
      </div>
    </section>
  );
}

const STEPS = [
  ["Free consultation", "Talk with me directly. I review your goals and tell you exactly what fits."],
  ["Enroll & upload", "Enroll in MyFreeScoreNow through our link and upload your report and ID in the portal."],
  ["One complete sweep", "We go after everything at once — and you can message me anytime along the way."],
  ["Get funded", "With your credit fixed, we map your path to $20k–$100k+ in funding."],
];

function How() {
  return (
    <section className="bg-cream py-20 lg:py-28" data-testid="how-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Eyebrow n="02">How it works</Eyebrow>
        <h2 className="f-display text-4xl sm:text-5xl mt-4 max-w-2xl">Four steps from consultation to capital.</h2>
        <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-14">
          {STEPS.map(([t, d], i) => (
            <li key={t} className="border-t-2 border-ink pt-6">
              <span className="f-mono text-gold text-sm font-semibold tracking-[0.2em]">0{i + 1}</span>
              <h3 className="f-display text-2xl mt-3">{t}</h3>
              <p className="text-[15px] text-muted7 mt-3 leading-relaxed">{d}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function ResultCard({ r }) {
  return (
    <article className="bg-white border border-tanline lift" data-testid="result-card">
      {r.image && <div className="aspect-[4/3] bg-tan overflow-hidden"><img src={r.image.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${r.image}` : r.image} alt={r.title || "Client result screenshot"} loading="lazy" className="w-full h-full object-cover" /></div>}
      <div className="p-6">
        <p className="eyebrow text-red7 text-[10px]">{r.category}</p>
        <h3 className="f-display text-xl mt-2">{r.title}</h3>
        {r.line && <p className="text-sm text-muted7 mt-2">{r.line}</p>}
        {(r.program || r.days || r.before) && (
          <p className="f-mono text-[10px] tracking-[0.12em] uppercase text-goldtext mt-4 pt-3 border-t border-tanline">
            {[r.program, r.days && `${r.days} days`, r.before && r.after && `${r.before} → ${r.after}`].filter(Boolean).join(" · ")}
          </p>
        )}
      </div>
    </article>
  );
}

function ResultsPreview({ results }) {
  if (!results?.length) return null;
  return (
    <section className="bg-white py-20 lg:py-28" data-testid="results-preview-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Eyebrow n="03">Client outcomes</Eyebrow>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mt-4">
          <h2 className="f-display text-4xl sm:text-5xl max-w-2xl">The records speak for themselves.</h2>
          <Btn as={Link} to="/results" variant="outline" arrow data-testid="view-all-results-btn">View All Results</Btn>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">{results.slice(0, 3).map((r, i) => <ResultCard key={i} r={r} />)}</div>
      </div>
    </section>
  );
}

function PriceCard({ p, style }) {
  const S = {
    express: { box: "bg-ink text-white border-ink on-dark", label: "text-gold", btn: "gold", sub: "text-white/60", dot: "bg-gold" },
    popular: { box: "bg-white border-red7 border-2", label: "text-red7", btn: "red", sub: "text-muted7", dot: "bg-red7" },
    affordable: { box: "bg-white border-tanline", label: "text-goldtext", btn: "black", sub: "text-muted7", dot: "bg-ink" },
  }[style];
  return (
    <article className={`brackets border p-8 lift flex flex-col ${S.box}`} data-testid={`price-card-${p.key}`}>
      <p className={`eyebrow ${S.label}`}>{p.label}</p>
      <h3 className="f-display text-3xl mt-3">{p.name}</h3>
      <p className="f-display text-6xl font-semibold mt-5 gold-foil leading-none py-1" data-testid={`price-${p.key}`}>{money(p.price)}</p>
      <p className={`text-sm mt-2 ${S.sub}`}>or split it interest-free with Afterpay.</p>
      <ul className="mt-7 space-y-3 flex-1">
        {p.bullets.map((b) => <li key={b} className="flex items-center gap-3 text-[15px]"><span className={`w-1.5 h-1.5 rotate-45 ${S.dot}`} />{b}</li>)}
      </ul>
      <Btn as={Link} to={`/consultation?plan=${p.key}`} variant={S.btn} arrow className="mt-8 w-full" data-testid={`start-${p.key}-btn`} onClick={() => track("cta_click", { label: `start_${p.key}` })}>Start Now</Btn>
    </article>
  );
}

function Pricing({ site }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((es) => { if (es[0].isIntersecting) { track("pricing_view"); io.disconnect(); } }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const plans = site?.pricing || [];
  const main = plans.filter((p) => ["express", "popular", "affordable"].includes(p.key));
  const sweep = plans.find((p) => p.key === "sweep");
  const square = site?.squareUrl || DEFAULT_SQUARE;
  return (
    <section id="pricing" ref={ref} className="bg-cream py-20 lg:py-28 scroll-mt-20" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Eyebrow n="04">Pricing</Eyebrow>
        <h2 className="f-display text-4xl sm:text-5xl mt-4 max-w-3xl">The fastest & most affordable in the industry.</h2>
        <p className="text-muted7 mt-4 text-base sm:text-lg">Pick your speed. Split any program into interest-free installments — anyone can start today.</p>
        <div className="grid md:grid-cols-3 gap-5 mt-12">{main.map((p) => <PriceCard key={p.key} p={p} style={p.key} />)}</div>
        {sweep && (
          <div className="bg-white border border-tanline border-l-4 !border-l-red7 p-7 sm:p-8 mt-5 flex flex-col md:flex-row md:items-center justify-between gap-6 lift" data-testid="sweep-card">
            <div>
              <p className="eyebrow text-red7">{sweep.label || "For everyone"}</p>
              <h3 className="f-display text-3xl mt-2">Inquiry Sweep <span className="gold-foil font-semibold">{money(sweep.price)}</span></h3>
              <p className="text-muted7 mt-2">{sweep.bullets?.[0] || "Unverified hard inquiries removed across all three bureaus."}</p>
            </div>
            <Btn as={Link} to="/consultation?plan=sweep" variant="red" arrow data-testid="get-sweep-btn">Get My Sweep</Btn>
          </div>
        )}
        <div className="bg-ink text-white p-7 sm:p-10 mt-5 flex flex-col lg:flex-row lg:items-center justify-between gap-8" data-testid="payment-strip">
          <div>
            <h3 className="f-display text-3xl">Pay your way — securely through Square.</h3>
            <div className="flex flex-wrap gap-2 mt-5">
              {["Any debit or credit card", "Cash App Pay", "Apple Pay", "Afterpay · interest-free installments"].map((t) => <span key={t} className="border border-white/25 px-3 py-2 text-xs f-mono tracking-[0.1em] uppercase text-white/85">{t}</span>)}
            </div>
            <p className="text-sm text-white/60 mt-4">Need a different plan? Message me — I'll work with you.</p>
          </div>
          <Btn as="a" href={square} target="_blank" rel="noopener noreferrer" variant="gold" arrow data-testid="pay-now-btn" onClick={() => track("cta_click", { label: "pay_now" })}>Pay Now</Btn>
        </div>
      </div>
    </section>
  );
}

export function ReviewCard({ r }) {
  return (
    <figure className="bg-tancard border border-tanline p-7 lift" data-testid="review-card">
      <p className="text-gold tracking-[0.2em]" aria-label={`${r.stars || 5} stars`}>{"★".repeat(r.stars || 5)}</p>
      <blockquote className="f-display text-xl mt-4 leading-snug">“{r.text}”</blockquote>
      <figcaption className="f-mono text-[11px] tracking-[0.14em] uppercase text-muted7 mt-5">{r.name}{r.program ? ` · ${r.program}` : ""}</figcaption>
    </figure>
  );
}

function Reviews({ site }) {
  if (!site?.reviews?.length) return null;
  const st = site.reviewStats || {};
  return (
    <section id="reviews" className="bg-white py-20 lg:py-28 scroll-mt-20" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Eyebrow n="05">5-star reviews</Eyebrow>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mt-4">
          <div>
            <h2 className="f-display text-4xl sm:text-5xl">In their words.</h2>
            {st.rating && st.count && <p className="mt-3 text-muted7"><span className="text-gold tracking-[0.2em]">★★★★★</span> {st.rating} from {st.count} verified Google reviews</p>}
          </div>
          <Btn as={Link} to="/results#reviews" variant="outline" arrow data-testid="all-reviews-btn">All Reviews</Btn>
        </div>
        <div className="grid md:grid-cols-3 gap-5 mt-12">{site.reviews.slice(0, 3).map((r, i) => <ReviewCard key={i} r={r} />)}</div>
      </div>
    </section>
  );
}

function ReferBlock({ payout = 100 }) {
  return (
    <section className="bg-cream py-20 lg:py-28" data-testid="refer-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <Eyebrow n="06">Refer & earn</Eyebrow>
          <h2 className="f-display text-4xl sm:text-5xl mt-4">Know someone who needs this? Get <span className="text-red7">${payout}</span>.</h2>
          <p className="text-muted7 mt-5 text-base sm:text-lg max-w-lg">Anyone can refer — you don't need to be a client. Send their name, number and goal. Get ${payout} on Cash App when they enroll.</p>
          <div className="flex flex-wrap items-center gap-4 mt-8">
            <Btn as={Link} to="/refer" variant="red" arrow data-testid="refer-now-btn">Refer Someone Now</Btn>
            <span className="f-mono text-[11px] tracking-[0.14em] uppercase text-muted7">Takes 30 seconds</span>
          </div>
        </div>
        <div className="grid sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3 gap-4">
          {["Their name, number & goal", "I reach out personally", `They enroll — you get $${payout}`].map((t, i) => (
            <div key={t} className="bg-white border border-tanline p-6 lift">
              <span className="f-display text-4xl text-gold">{i + 1}</span>
              <p className="f-display text-lg mt-3 leading-snug">{t}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Faq({ items }) {
  const list = items?.length ? items.map((f) => [f.q, f.a]) : FAQ;
  return (
    <section className="bg-white py-20 lg:py-28" data-testid="faq-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-[.8fr_1.2fr] gap-12">
        <div>
          <Eyebrow n="07">FAQ</Eyebrow>
          <h2 className="f-display text-4xl sm:text-5xl mt-4">Questions before you get started.</h2>
          <Btn as={Link} to="/consultation" arrow className="mt-8" data-testid="faq-consult-btn">Book Your Free Consultation</Btn>
        </div>
        <Accordion type="single" collapsible className="border-t border-tanline">
          {list.map(([q, a], i) => (
            <AccordionItem key={q} value={`f${i}`} className="border-b border-tanline">
              <AccordionTrigger className="f-display text-lg sm:text-xl text-left py-6 hover:no-underline" data-testid={`faq-q-${i}`}>{q}</AccordionTrigger>
              <AccordionContent className="text-[15px] text-muted7 leading-relaxed pb-6">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

export function FinalCta() {
  return (
    <section className="bg-ink text-white relative overflow-hidden py-24 lg:py-32" data-testid="final-cta">
      <span className="absolute -right-10 -top-24 f-display font-semibold text-gold opacity-[.09] leading-none select-none pointer-events-none text-[420px] lg:text-[560px]" aria-hidden="true">7</span>
      <div className="relative max-w-4xl mx-auto px-5 text-center">
        <h2 className="f-display text-4xl sm:text-5xl lg:text-6xl">Ready to restructure your financial profile?</h2>
        <p className="text-white/70 mt-6 text-base sm:text-lg max-w-2xl mx-auto">Book a free, no-obligation consultation with me. We'll map your sweep and your path to funding.</p>
        <Btn as={Link} to="/consultation" variant="gold" arrow className="mt-10" data-testid="final-cta-btn">Book With Seven</Btn>
        <Ornament className="mt-14 mb-5" />
        <p className="f-mono text-[10px] sm:text-[11px] tracking-[0.2em] text-gold">BUILDING BETTER CREDIT · FUNDING YOUR FUTURE · CREATING FINANCIAL FREEDOM</p>
      </div>
    </section>
  );
}

export default function Home() {
  useSeo(null, "Credit repair, inquiry sweeps and $20k–$100k+ funding — handled personally by Seven. The fastest and most affordable in the industry.");
  const { data: site } = useSite();
  return (
    <>
      <Hero />
      <Ticker />
      <Services />
      <How />
      <ResultsPreview results={site?.results} />
      <Pricing site={site} />
      <Reviews site={site} />
      <ReferBlock payout={site?.referralPayout || 100} />
      <Faq items={site?.faq} />
      <FinalCta />
    </>
  );
}
